import { useState, useEffect } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { fetchPageBySlug, getResizedImageUrl } from '../lib/cms';
import Gallery from '../components/Gallery';
import ContactForm from '../components/ContactForm';
import ContentWithCTA from '../components/ContentWithCTA';

export default function YdelseDetail({ slugOverride }) {
  const { slug: paramSlug } = useParams();
  const slug = slugOverride || paramSlug;
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  // Valid slugs for services
  const validSlugs = [
    'belaegning',
    'entreprenoer',
    'stoebearbejde',
    'betonarbejde',
    'murerarbejde',
    'totalloesninger',
    'anlaegsarbejde',
    'ny-indkoersel',
    'ny-terrasse',
    'stoettemur',
    'haveanlaeg'
  ];

  useEffect(() => {
    async function loadPage() {
      setLoading(true);
      setNotFound(false);

      if (!validSlugs.includes(slug)) {
        setNotFound(true);
        setLoading(false);
        return;
      }

      const data = await fetchPageBySlug(slug);
      if (!data) {
        setNotFound(true);
      } else {
        setPageData(data);
      }
      setLoading(false);
    }
    loadPage();
  }, [slug]);

  useEffect(() => {
    if (pageData) {
      const title = pageData.meta_title || `${pageData.title} - Larsen & Kristensen`;
      const description = pageData.meta_description || '';
      
      // Set Document Title
      document.title = title;

      // Helper to set or create meta tags
      const setMetaTag = (name, content, isProperty = false) => {
        const selector = isProperty ? `meta[property="${name}"]` : `meta[name="${name}"]`;
        let element = document.querySelector(selector);
        if (!element) {
          element = document.createElement('meta');
          if (isProperty) {
            element.setAttribute('property', name);
          } else {
            element.setAttribute('name', name);
          }
          document.head.appendChild(element);
        }
        element.setAttribute('content', content || '');
      };

      // Set standard meta tags
      setMetaTag('description', description);

      // Set Open Graph tags
      setMetaTag('og:title', title, true);
      setMetaTag('og:description', description, true);
      // Set Open Graph Image to empty string as requested, or fallback to hero image if desired
      setMetaTag('og:image', '', true);
      setMetaTag('og:type', 'website', true);
      setMetaTag('og:url', window.location.href, true);

      // Set Twitter Card tags
      setMetaTag('twitter:card', 'summary_large_image');
      setMetaTag('twitter:title', title);
      setMetaTag('twitter:description', description);
      setMetaTag('twitter:image', '');

      // Set Canonical Link
      let canonicalLink = document.querySelector('link[rel="canonical"]');
      if (!canonicalLink) {
        canonicalLink = document.createElement('link');
        canonicalLink.setAttribute('rel', 'canonical');
        document.head.appendChild(canonicalLink);
      }
      canonicalLink.setAttribute('href', window.location.href);
    }
  }, [pageData]);

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="h-12 w-12 animate-spin rounded-full border-4 border-[#163A2B] border-t-transparent"></div>
      </div>
    );
  }

  if (notFound) {
    return <Navigate to="/404" replace />;
  }

  const heroBg = pageData?.hero_image?.[0] || 'https://assets.auxodev.dk/assets/54439585-0ea9-462f-a6c1-e69db40e10d7/danish-house-modern-landscaped-garden-driveway.jpg';
  const resizedHeroBg = getResizedImageUrl(heroBg, 1600, 80);

  // Helper to get a friendly icon for each service
  const getServiceIcon = (s) => {
    switch (s) {
      case 'belaegning': return 'fa-solid fa-border-all';
      case 'ny-indkoersel': return 'fa-solid fa-road';
      case 'ny-terrasse': return 'fa-solid fa-couch';
      case 'stoettemur': return 'fa-solid fa-cubes';
      case 'haveanlaeg': return 'fa-solid fa-tree';
      case 'entreprenoer': return 'fa-solid fa-helmet-safety';
      case 'stoebearbejde': return 'fa-solid fa-layer-group';
      case 'betonarbejde': return 'fa-solid fa-fill-drip';
      case 'murerarbejde': return 'fa-solid fa-trowel-bricks';
      case 'totalloesninger': return 'fa-solid fa-screwdriver-wrench';
      case 'anlaegsarbejde': return 'fa-solid fa-seedling';
      default: return 'fa-solid fa-screwdriver-wrench';
    }
  };

  return (
    <div className="flex flex-col min-h-screen">
      
      {/* Page Header / Hero */}
      <section className="relative bg-[#1C1E1D] py-16 sm:py-24 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src={resizedHeroBg}
            alt={pageData?.title}
            className="h-full w-full object-cover object-center opacity-50"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#163A2B]/50 via-[#1C1E1D]/70 to-[#1C1E1D]/90"></div>
        </div>

        <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 text-center">
          <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-[#163A2B] bg-[#F4F1EA] px-3.5 py-1.5 rounded-full">
            <i className={getServiceIcon(slug)} /> Vores Ydelser
          </span>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mt-4 font-display">
            {pageData?.title}
          </h1>
          <p className="mt-4 text-base sm:text-lg text-[#F4F1EA]/80 max-w-2xl mx-auto leading-relaxed">
            {pageData?.intro}
          </p>
        </div>
      </section>

      {/* Main Content & Sidebar */}
      <section className="py-16 bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            
            {/* Left Column: Description & Gallery */}
            <div className="lg:col-span-8 space-y-8">
              <ContentWithCTA 
                html={pageData?.content_html} 
                className="text-base text-[#1C1E1D]/80 leading-relaxed space-y-6"
                showCTA={true}
                currentSlug={slug}
              />

              {/* Dynamic Gallery connected via slug */}
              <Gallery pageSlug={slug} />
            </div>

            {/* Right Column: Sidebar with Contact info & Navigation */}
            <div className="lg:col-span-4 space-y-8 lg:sticky lg:top-6">
              
              {/* Quick Navigation - NO hover effects on non-clickable items */}
              <div className="bg-[#F4F1EA] border border-[#E2E2E0] rounded-lg p-6 sm:p-8">
                <h3 className="text-sm font-bold uppercase tracking-wider text-[#1C1E1D] border-b border-[#E2E2E0] pb-3 mb-4 font-display">
                  Alle Ydelser
                </h3>
                <ul className="space-y-2">
                  {[
                    { 
                      name: 'Belægning', 
                      path: '/ydelser/belaegning',
                      subServices: [
                        { name: 'Ny indkørsel', path: '/ydelser/belaegning/ny-indkoersel' },
                        { name: 'Ny terrasse', path: '/ydelser/belaegning/ny-terrasse' },
                        { name: 'Støttemur', path: '/ydelser/belaegning/stoettemur' }
                      ]
                    },
                    { name: 'Entreprenør', path: '/ydelser/entreprenoer' },
                    { name: 'Støbearbejde', path: '/ydelser/stoebearbejde' },
                    { name: 'Betonarbejde', path: '/ydelser/betonarbejde' },
                    { name: 'Murerarbejde', path: '/ydelser/murerarbejde' },
                    { name: 'Totalløsninger', path: '/ydelser/totalloesninger' },
                    { 
                      name: 'Anlægsarbejde', 
                      path: '/ydelser/anlaegsarbejde',
                      subServices: [
                        { name: 'Haveanlæg', path: '/ydelser/anlaegsarbejde/haveanlaeg' }
                      ]
                    },
                  ].map((item) => {
                    const isCurrent = item.path === `/ydelser/${slug}` || 
                      ((slug === 'ny-indkoersel' || slug === 'ny-terrasse' || slug === 'stoettemur') && item.path === '/ydelser/belaegning') ||
                      (slug === 'haveanlaeg' && item.path === '/ydelser/anlaegsarbejde');
                    const isSubOpen = (item.name === 'Belægning' && (slug === 'ny-indkoersel' || slug === 'ny-terrasse' || slug === 'stoettemur')) ||
                      (item.name === 'Anlægsarbejde' && (slug === 'haveanlaeg'));
                    return (
                      <li key={item.path} className="space-y-1.5 group/sidebar-item">
                        <Link
                          to={item.path}
                          className={`flex items-center justify-between px-4 py-3 rounded-md text-sm font-semibold transition-colors ${
                            isCurrent
                              ? 'bg-[#163A2B] text-white'
                              : 'bg-white border border-[#E2E2E0] text-[#1C1E1D]/80 hover:bg-[#163A2B]/5 hover:text-[#163A2B]'
                          }`}
                        >
                          <span>{item.name}</span>
                          <i className="fa-solid fa-chevron-right text-xs" />
                        </Link>
                        {item.subServices && (
                          <div className={`grid transition-all duration-300 ease-in-out ${
                            isSubOpen
                              ? 'grid-rows-[1fr] opacity-100 mt-1.5'
                              : 'grid-rows-[0fr] opacity-0 group-hover/sidebar-item:grid-rows-[1fr] group-hover/sidebar-item:opacity-100 group-hover/sidebar-item:mt-1.5'
                          }`}>
                            <div className="overflow-hidden space-y-1.5">
                              {item.subServices.map((sub) => {
                                const isSubCurrent = slug === sub.path.split('/').pop();
                                return (
                                  <Link
                                    key={sub.path}
                                    to={sub.path}
                                    className={`flex items-center justify-between pl-8 pr-4 py-2.5 rounded-md text-xs font-semibold transition-colors ${
                                      isSubCurrent
                                        ? 'bg-[#163A2B]/10 text-[#163A2B] border border-[#163A2B]/20'
                                        : 'bg-white border border-[#E2E2E0]/60 text-[#1C1E1D]/70 hover:bg-[#163A2B]/5 hover:text-[#163A2B]'
                                    }`}
                                  >
                                    <span>{sub.name}</span>
                                    <i className="fa-solid fa-angle-right text-[10px]" />
                                  </Link>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </div>

              {/* Direct Call Box - NO hover effects as it is not clickable */}
              <div className="bg-[#1C1E1D] text-white rounded-lg p-6 sm:p-8 border-l-4 border-[#163A2B]">
                <h3 className="text-lg font-bold font-display">Har du spørgsmål?</h3>
                <p className="text-xs text-[#F4F1EA]/70 mt-1">Vi står klar til at rådgive dig om dit projekt.</p>
                <div className="mt-6 flex items-center gap-4">
                  <div className="bg-[#163A2B] text-white p-3 rounded-md">
                    <i className="fa-solid fa-phone text-lg" />
                  </div>
                  <div>
                    <p className="text-xs text-[#F4F1EA]/60 uppercase font-bold">Ring direkte</p>
                    <p className="text-lg font-bold text-white">
                      <a href="tel:+4530713311" className="hover:underline whitespace-nowrap">30 71 33 11</a>
                    </p>
                  </div>
                </div>
              </div>

            </div>

          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-16 bg-[#F4F1EA] border-t border-[#E2E2E0]">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <h2 className="text-2xl sm:text-3xl font-bold text-[#1C1E1D] font-display">
              Få et uforpligtende tilbud på {pageData?.title?.toLowerCase()}
            </h2>
            <p className="text-sm text-[#1C1E1D]/70 mt-2">
              Udfyld formularen nedenfor, så kontakter vi dig hurtigst muligt for at drøfte dit projekt og udarbejde et skriftligt tilbud.
            </p>
          </div>
          <ContactForm />
        </div>
      </section>

    </div>
  );
}
